import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { xhrRequest } from '../lib/xhrRequest';
import { 
  DollarSign, Calendar, MessageSquare, QrCode, Link as LinkIcon, 
  Download, Copy, LogOut, Settings, List, Home, CreditCard, 
  AlertCircle, Loader2, CheckCircle, User, ChevronRight, Trophy,
  TrendingUp, Sparkles
} from 'lucide-react';
import Footer from '../components/Footer';

export default function DashboardPage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [justCopied, setJustCopied] = useState(false);
  const [challenge, setChallenge] = useState(null);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const BASE_URL = window.location.origin;
  const tipLink = `${BASE_URL}/u/${user?.publicSlug}`;

  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API}/dashboard/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (!cancelled) setStats(data);
        }
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchStats();
    return () => { cancelled = true; };
  }, [API, token]);

  // Pull current-month challenge so we can show a "You're in the running!"
  // banner without forcing the worker to click over to the leaderboard.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { ok, data } = await xhrRequest('GET', `${API}/challenge/current`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!cancelled && ok) setChallenge(data);
    })();
    return () => { cancelled = true; };
  }, [API, token]);

  const copyLink = async () => {
    const done = () => {
      setJustCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setJustCopied(false), 2000);
    };
    try {
      await navigator.clipboard.writeText(tipLink);
      done();
    } catch (err) {
      // Fallback for environments where clipboard is blocked
      const textArea = document.createElement('textarea');
      textArea.value = tipLink;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        done();
      } catch (e) {
        toast.error('Failed to copy link');
      }
      document.body.removeChild(textArea);
    }
  };

  const downloadQR = () => {
    const svg = document.getElementById('qr-code');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = 400;
      canvas.height = 400;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 400, 400);
      ctx.drawImage(img, 50, 50, 300, 300);
      
      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = `tip-${user?.publicSlug}-qr.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
    toast.success('QR code downloaded!');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Mobile Header */}
      <header className="sticky top-0 z-40 bg-[#050505]/95 backdrop-blur-xl border-b border-white/5 px-4 py-3 md:hidden">
        <div className="flex items-center justify-between">
          <h1 className="font-heading font-bold text-lg text-[#F5C542]">Golden Tips</h1>
          <button onClick={handleLogout} className="text-[#A1A1AA] hover:text-white">
            <LogOut size={20} />
          </button>
        </div>
      </header>

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside className="hidden md:flex flex-col w-64 h-screen sticky top-0 bg-[#0a0a0a] border-r border-white/5 p-6">
          <div className="mb-8">
            <h1 className="font-heading font-bold text-xl text-[#F5C542]">Golden Tips</h1>
          </div>

          <nav className="flex-1 space-y-1">
            <Link to="/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 text-white">
              <Home size={20} />
              <span>Dashboard</span>
            </Link>
            <Link to="/dashboard/tips" className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#A1A1AA] hover:bg-white/5 hover:text-white transition-colors">
              <List size={20} />
              <span>Tips History</span>
            </Link>
            <Link to="/dashboard/leaderboard" className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#A1A1AA] hover:bg-white/5 hover:text-white transition-colors" data-testid="sidebar-leaderboard-link">
              <Trophy size={20} />
              <span>Challenge</span>
            </Link>
            <Link to="/dashboard/settings" className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#A1A1AA] hover:bg-white/5 hover:text-white transition-colors" data-testid="sidebar-settings-link">
              <Settings size={20} />
              <span>Settings</span>
            </Link>
          </nav>

          <div className="pt-6 border-t border-white/5">
            <Link
              to="/dashboard/settings"
              className="flex items-center gap-3 mb-4 -mx-2 px-2 py-2 rounded-lg hover:bg-white/5 transition-colors group"
              data-testid="sidebar-user-block"
            >
              <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center overflow-hidden">
                {user?.photoUrl ? (
                  <img src={user.photoUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="font-bold">{user?.displayName?.[0]?.toUpperCase()}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{user?.displayName}</p>
                <p className="text-xs text-[#A1A1AA] truncate">{user?.serviceTitle}</p>
              </div>
              <ChevronRight size={16} className="text-[#52525B] group-hover:text-white transition-colors" />
            </Link>
            <button 
              onClick={handleLogout}
              className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors text-sm"
              data-testid="logout-button"
            >
              <LogOut size={16} />
              Sign out
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-8 pb-24 md:pb-8" data-testid="dashboard-main">
          <div className="max-w-5xl mx-auto">
            {/* Welcome Section */}
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-heading font-bold mb-2">
                Welcome back, {user?.displayName?.split(' ')[0]}
              </h2>
              <p className="text-[#A1A1AA]">Here&apos;s how your tips are doing</p>
            </div>

            {/* Payout Alert — distinguishes "started but didn't finish"
                from "never started" and surfaces a prominent CTA to
                resume onboarding. Workers with stripeAccountId set but
                stripeOnboarded=false dropped out mid-flow; they need to
                go back through Stripe's Account Link to finish KYC. */}
            {!user?.stripeOnboarded && (
              <div
                className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-5 mb-6"
                data-testid="payout-setup-banner"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <AlertCircle size={24} className="text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-amber-100 text-base">
                        {user?.stripeAccountId
                          ? 'Finish your payout setup'
                          : "You can't receive tips yet"}
                      </p>
                      <p className="text-sm text-amber-200/80 mt-1">
                        {user?.stripeAccountId
                          ? "You started Stripe onboarding but haven't completed identity verification. Tips can't be paid out until this is finished — it only takes a minute."
                          : 'Connect your bank account through Stripe to start receiving tips. Takes about a minute.'}
                      </p>
                    </div>
                  </div>
                  <Link
                    to="/onboarding/payouts"
                    className="btn-primary !w-auto !px-5 !py-3 text-sm flex items-center justify-center gap-2 md:flex-shrink-0"
                    data-testid="complete-payout-setup-button"
                  >
                    <CreditCard size={16} />
                    {user?.stripeAccountId ? 'Complete payout setup' : 'Connect payouts'}
                  </Link>
                </div>
              </div>
            )}

            {/* Challenge status banner — dashboard-native "You're in the
                running!" strip. Shows one of three states:
                  1. Ranked  → medal + category + projected payout ($ live)
                  2. Almost  → tip count vs qualifying threshold, encouraging
                               copy to push for the finish line
                  3. Hidden  → pool is zero or user not payout-ready
                Kept intentionally slim so it doesn't crowd the QR/link. */}
            {challenge && challenge.poolCents > 0 && user?.stripeOnboarded && (() => {
              const findMe = (list) => list.find((e) => e.userId === user?.id);
              const meTop = findMe(challenge.topEarner);
              const meRate = findMe(challenge.highestRate);
              const best = meTop && meRate
                ? (meTop.payoutCents >= meRate.payoutCents ? { ...meTop, category: 'Top Earner' } : { ...meRate, category: 'Highest Tip Rate' })
                : (meTop ? { ...meTop, category: 'Top Earner' } : (meRate ? { ...meRate, category: 'Highest Tip Rate' } : null));
              const medal = best ? ['🥇', '🥈', '🥉'][best.rank - 1] : null;
              const fmt = (c) =>
                `$${(Math.max(0, c) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

              if (best) {
                return (
                  <Link
                    to="/dashboard/leaderboard"
                    className="group block bg-gradient-to-r from-[#F5C542]/15 via-[#F5C542]/5 to-transparent border border-[#F5C542]/30 rounded-xl p-4 mb-6 hover:from-[#F5C542]/20 transition-colors"
                    data-testid="challenge-banner-ranked"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl" aria-hidden>{medal}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-white text-sm md:text-base">
                          You&apos;re currently ranked #{best.rank} in {best.category}
                        </p>
                        <p className="text-xs text-[#F5C542]/90 mt-0.5">
                          {best.payoutCents > 0
                            ? <>Projected payout: <span className="font-mono font-bold">{fmt(best.payoutCents)}</span> · pool {fmt(challenge.poolCents)}</>
                            : <>Pool is at {fmt(challenge.poolCents)} — payouts unlock at {fmt(challenge.tierThresholdCents)}</>}
                        </p>
                      </div>
                      <div className="hidden md:flex items-center gap-1 text-xs text-white/70 group-hover:text-white">
                        <Trophy size={14} /> View leaderboard
                      </div>
                      <ChevronRight size={16} className="text-[#F5C542] flex-shrink-0" />
                    </div>
                  </Link>
                );
              }

              // Not ranked — encourage push to qualify
              const tipsThisMonth = stats?.tipsLast30Days || 0;
              const needed = Math.max(0, challenge.minUniqueTippers - tipsThisMonth);
              return (
                <Link
                  to="/dashboard/leaderboard"
                  className="group block bg-white/[0.03] border border-white/10 rounded-xl p-4 mb-6 hover:bg-white/[0.06] transition-colors"
                  data-testid="challenge-banner-almost"
                >
                  <div className="flex items-center gap-3">
                    <Sparkles size={22} className="text-[#F5C542]" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-white text-sm md:text-base">
                        {needed > 0
                          ? <>You&apos;re {needed} tip{needed === 1 ? '' : 's'} away from the {challenge.periodKey} leaderboard</>
                          : <>You&apos;re not in the top 3 yet — keep tipping in</>}
                      </p>
                      <p className="text-xs text-[#A1A1AA] mt-0.5">
                        Pool this month: <span className="text-[#F5C542] font-mono">{fmt(challenge.poolCents)}</span>
                        {' · '}Top 3 in each category split the winnings.
                      </p>
                    </div>
                    <ChevronRight size={16} className="text-[#52525B] group-hover:text-white flex-shrink-0" />
                  </div>
                </Link>
              );
            })()}

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
              <div className="stat-card highlight" data-testid="total-tips-stat">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-[#F5C542]/10 flex items-center justify-center">
                    <DollarSign size={18} className="text-[#F5C542]" />
                  </div>
                </div>
                <p className="text-2xl md:text-3xl font-heading font-bold text-[#F5C542]">
                  {loading ? '...' : formatCurrency(stats?.totalAmountCents || 0)}
                </p>
                <p className="text-sm text-[#A1A1AA]">Total tips</p>
              </div>

              <div className="stat-card" data-testid="tips-30-days-stat">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                    <Calendar size={18} className="text-white" />
                  </div>
                </div>
                <p className="text-2xl md:text-3xl font-heading font-bold">
                  {loading ? '...' : formatCurrency(stats?.amountLast30DaysCents || 0)}
                </p>
                <p className="text-sm text-[#A1A1AA]">Last 30 days</p>
              </div>

              <div className="stat-card col-span-2 md:col-span-1" data-testid="messages-stat">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                    <MessageSquare size={18} className="text-white" />
                  </div>
                </div>
                <p className="text-2xl md:text-3xl font-heading font-bold">
                  {loading ? '...' : stats?.messagesCount || 0}
                </p>
                <p className="text-sm text-[#A1A1AA]">Messages received</p>
              </div>
            </div>

            {/* QR Code & Link Section */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="stat-card" data-testid="qr-code-section">
                <h3 className="font-heading font-semibold mb-4 flex items-center gap-2">
                  <QrCode size={20} />
                  Your QR Code
                </h3>
                <div className="flex flex-col items-center">
                  <div className="qr-container mb-4">
                    <QRCodeSVG 
                      id="qr-code"
                      value={tipLink} 
                      size={180}
                      level="H"
                      includeMargin={false}
                    />
                  </div>
                  <button 
                    onClick={downloadQR}
                    className="btn-secondary flex items-center gap-2"
                    data-testid="download-qr-button"
                  >
                    <Download size={16} />
                    Download QR
                  </button>
                </div>
              </div>

              <div className="stat-card" data-testid="tip-link-section">
                <h3 className="font-heading font-semibold mb-4 flex items-center gap-2">
                  <LinkIcon size={20} />
                  Your Golden Tips Link
                </h3>
                <p className="text-sm text-[#A1A1AA] mb-4">
                  Share this link with your customers to receive tips instantly.
                </p>
                <div className="bg-white/5 rounded-xl p-3 flex items-center gap-3 mb-4">
                  <code className="flex-1 text-sm text-[#F5C542] truncate" data-testid="tip-link-display">
                    {tipLink}
                  </code>
                  <button 
                    onClick={copyLink}
                    className={`transition-colors ${justCopied ? 'text-green-400' : 'text-[#A1A1AA] hover:text-white'}`}
                    data-testid="copy-link-button"
                    aria-label={justCopied ? 'Link copied' : 'Copy link'}
                  >
                    {justCopied ? <CheckCircle size={18} /> : <Copy size={18} />}
                  </button>
                </div>
                <Link 
                  to={`/u/${user?.publicSlug}`}
                  target="_blank"
                  className="btn-secondary flex items-center justify-center gap-2 w-full"
                  data-testid="preview-tip-page-button"
                >
                  Preview your page
                </Link>
              </div>
            </div>
          </div>
          <Footer className="mt-10 max-w-5xl mx-auto" />
        </main>
      </div>

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-[#0a0a0a]/95 backdrop-blur-xl border-t border-white/5 px-4 py-2 flex items-center justify-around z-50">
        <Link to="/dashboard" className="flex flex-col items-center gap-1 py-2 px-4 text-[#F5C542]">
          <Home size={20} />
          <span className="text-xs">Home</span>
        </Link>
        <Link to="/dashboard/tips" className="flex flex-col items-center gap-1 py-2 px-4 text-[#A1A1AA]">
          <List size={20} />
          <span className="text-xs">Tips</span>
        </Link>
        <Link to={`/u/${user?.publicSlug}`} target="_blank" className="flex flex-col items-center gap-1 py-2 px-4 text-[#A1A1AA]">
          <QrCode size={20} />
          <span className="text-xs">My Page</span>
        </Link>
        <Link to="/dashboard/settings" className="flex flex-col items-center gap-1 py-2 px-4 text-[#A1A1AA]" data-testid="mobile-nav-settings">
          <User size={20} />
          <span className="text-xs">Settings</span>
        </Link>
      </nav>
    </div>
  );
}
